import type { ProductListDrawerFilters } from '../productFilterTypes';
import type { CategoryFilterOption } from '../productsMeta';
import { FILTERS_COPY, type FilterWords } from './filterLabels';

/**
 * The category picker's tree logic (BOS-202).
 *
 * The server matches a category with everything beneath it, at any depth
 * (bos-api BOS-173), so picking a category means its whole branch. The picker
 * keeps the selection as the fewest ids that say it: a picked parent stands
 * for its branch, and a parent whose every child is picked is picked itself,
 * which the field then names once as "Electronics (all)". That also sends the
 * parent's own products, which is what "all of Electronics" reads as.
 *
 * A parent's row is ticked, partly ticked ("2 of 19", counted in the
 * bottom-level categories under it) or unticked. Unticking one category
 * inside a picked branch keeps the rest of the branch picked.
 */

/**
 * The category picker's key for "Uncategorized". Category ids are
 * server-made, so this never names a real category.
 */
export const NO_CATEGORY_KEY = '__no-category__';

/** The drawer's category fields, which the category picker edits as one value. */
export type CategoryFilterValue = Pick<
    ProductListDrawerFilters,
    'categoryIds' | 'noCategory'
>;

export type CategoryTreeNode = {
    id: string;
    name: string;
    /** 0 for a top-level category. */
    depth: number;
    parentId: string | null;
    /** The names above it, from the top-level category down to its parent. */
    ancestorNames: string[];
    childIds: string[];
    /** The bottom-level categories under it, or itself when it has none. */
    leafIds: string[];
};

export type CategoryTree = {
    /** Pre-order: each category followed by everything under it. */
    nodes: CategoryTreeNode[];
    byId: ReadonlyMap<string, CategoryTreeNode>;
};

/**
 * The tree behind the flat, depth-tagged option list `flattenPickerCategories`
 * builds. That list is well formed; should a depth skip a level anyway, the
 * category hangs off the nearest one above it, or is top-level when there is
 * none, so the picker shows what it can.
 */
export const buildCategoryTree = (
    options: readonly CategoryFilterOption[],
): CategoryTree => {
    const nodes: CategoryTreeNode[] = [];
    const byId = new Map<string, CategoryTreeNode>();
    const ancestors: CategoryTreeNode[] = [];

    for (const option of options) {
        if (byId.has(option.id)) {
            continue;
        }
        while (ancestors.length > option.depth) {
            ancestors.pop();
        }
        const parent = ancestors[ancestors.length - 1] ?? null;
        const node: CategoryTreeNode = {
            id: option.id,
            name: option.name,
            depth: ancestors.length,
            parentId: parent?.id ?? null,
            ancestorNames: ancestors.map((ancestor) => ancestor.name),
            childIds: [],
            leafIds: [],
        };
        parent?.childIds.push(node.id);
        nodes.push(node);
        byId.set(node.id, node);
        ancestors.push(node);
    }

    // Reverse pre-order reaches every category after everything under it.
    for (const node of [...nodes].reverse()) {
        node.leafIds =
            node.childIds.length === 0
                ? [node.id]
                : node.childIds.flatMap((id) => byId.get(id)!.leafIds);
    }

    return { nodes, byId };
};

/** A top-level category and everything under it, in tree order. */
export type CategoryTreeSection = {
    root: CategoryTreeNode;
    descendants: CategoryTreeNode[];
};

/** The picker's sections while nothing is typed: one per top-level category. */
export const categoryTreeSections = (
    tree: CategoryTree,
): CategoryTreeSection[] => {
    const sections: CategoryTreeSection[] = [];
    for (const node of tree.nodes) {
        if (node.parentId === null) {
            sections.push({ root: node, descendants: [] });
        } else {
            // Pre-order: everything under a root follows it, before the next.
            sections[sections.length - 1]?.descendants.push(node);
        }
    }
    return sections;
};

const uniqueIds = (ids: Iterable<string>): string[] => [...new Set(ids)];

/** The known ids and everything under them. */
const coveredIds = (
    ids: readonly string[],
    tree: CategoryTree,
): Set<string> => {
    const covered = new Set<string>();
    const pending = ids.filter((id) => tree.byId.has(id));
    while (pending.length > 0) {
        const id = pending.pop()!;
        if (!covered.has(id)) {
            covered.add(id);
            pending.push(...tree.byId.get(id)!.childIds);
        }
    }
    return covered;
};

/** Every category reached, with each parent whose children all are. */
const fullyCoveredIds = (
    ids: readonly string[],
    tree: CategoryTree,
): Set<string> => {
    const covered = coveredIds(ids, tree);
    for (const node of [...tree.nodes].reverse()) {
        if (
            node.childIds.length > 0 &&
            node.childIds.every((id) => covered.has(id))
        ) {
            covered.add(node.id);
        }
    }
    return covered;
};

/**
 * The fewest ids that pick the same categories: nothing inside a picked
 * branch, and a parent in place of a full set of children. Known ids come in
 * tree order; ids the tree doesn't know (a deleted category in a shared link,
 * or any id while the tree loads) follow as they were, so they stay picked.
 */
export const normalizeCategoryIds = (
    ids: readonly string[],
    tree: CategoryTree,
): string[] => {
    const covered = fullyCoveredIds(ids, tree);
    const known = tree.nodes
        .filter(
            (node) =>
                covered.has(node.id) &&
                (node.parentId === null || !covered.has(node.parentId)),
        )
        .map((node) => node.id);
    const unknown = ids.filter((id) => !tree.byId.has(id));
    return uniqueIds([...known, ...unknown]);
};

export type CategoryTickState = {
    state: 'ticked' | 'partial' | 'unticked';
    /** Bottom-level categories picked under it. */
    picked: number;
    /** Bottom-level categories under it. */
    total: number;
};

/** Every category's tick state for the picked `ids`. */
export const getCategoryTickStates = (
    ids: readonly string[],
    tree: CategoryTree,
): Map<string, CategoryTickState> => {
    const covered = fullyCoveredIds(ids, tree);
    return new Map(
        tree.nodes.map((node) => {
            const picked = node.leafIds.filter((id) => covered.has(id)).length;
            const state = covered.has(node.id)
                ? 'ticked'
                : picked > 0
                  ? 'partial'
                  : 'unticked';
            return [
                node.id,
                { state, picked, total: node.leafIds.length },
            ] as const;
        }),
    );
};

/** A row with nothing under it to count: "Uncategorized", or an id not in the tree. */
export const UNTICKED: CategoryTickState = {
    state: 'unticked',
    picked: 0,
    total: 0,
};
export const TICKED: CategoryTickState = {
    state: 'ticked',
    picked: 0,
    total: 0,
};

/** How far a row steps in per level of the tree below its section's own row. */
const INDENT_STEP_PX = 12;

export const categoryIndentPx = (indent: number): number =>
    indent * INDENT_STEP_PX;

/**
 * Ticks or unticks one category. Ticking picks its whole branch. Unticking
 * drops its branch; when it sat inside a picked branch, the rest of that
 * branch stays picked, spelled out as the siblings along the way down.
 */
export const toggleCategory = (
    ids: readonly string[],
    id: string,
    tree: CategoryTree,
): string[] => {
    const current = normalizeCategoryIds(ids, tree);
    const node = tree.byId.get(id);
    if (!node) {
        return current.includes(id)
            ? current.filter((picked) => picked !== id)
            : [...current, id];
    }
    if (!coveredIds(current, tree).has(id)) {
        return normalizeCategoryIds([...current, id], tree);
    }

    // The picked branch it sits in: itself, or its nearest picked ancestor.
    const path: CategoryTreeNode[] = [node];
    while (!current.includes(path[0]!.id)) {
        path.unshift(tree.byId.get(path[0]!.parentId!)!);
    }
    const kept = path.slice(0, -1).flatMap((step, index) => {
        const next = path[index + 1]!.id;
        return step.childIds.filter((childId) => childId !== next);
    });
    return normalizeCategoryIds(
        [...current.filter((picked) => picked !== path[0]!.id), ...kept],
        tree,
    );
};

/**
 * The picker's selection: "Uncategorized", every ticked row (a partly ticked
 * parent is not), then the ids the tree doesn't know.
 */
export const toCategoryPickerKeys = (
    { categoryIds, noCategory }: CategoryFilterValue,
    tree: CategoryTree,
): string[] => {
    const covered = fullyCoveredIds(categoryIds, tree);
    return [
        ...(noCategory ? [NO_CATEGORY_KEY] : []),
        ...tree.nodes
            .filter((node) => covered.has(node.id))
            .map((node) => node.id),
        ...uniqueIds(categoryIds.filter((id) => !tree.byId.has(id))),
    ];
};

/**
 * Reads the picker's new selection as the rows it ticked or unticked since
 * `value`, and applies each. An empty selection (the field's clear) clears
 * everything.
 */
export const applyCategoryPickerChange = (
    value: CategoryFilterValue,
    keys: Iterable<unknown>,
    tree: CategoryTree,
): CategoryFilterValue => {
    const next = new Set([...keys].map(String));
    if (next.size === 0) {
        return { categoryIds: [], noCategory: false };
    }
    const current = new Set(toCategoryPickerKeys(value, tree));
    const toggled = [
        ...[...current].filter((key) => !next.has(key)),
        ...[...next].filter((key) => !current.has(key)),
    ].filter((key) => key !== NO_CATEGORY_KEY);

    return {
        categoryIds: toggled.reduce(
            (ids, key) => toggleCategory(ids, key, tree),
            normalizeCategoryIds(value.categoryIds, tree),
        ),
        noCategory: next.has(NO_CATEGORY_KEY),
    };
};

/** One picked value as the field's tags and the tags row name it. */
export type CategorySelectionItem =
    | { kind: 'uncategorized' }
    | {
          kind: 'category';
          id: string;
          name: string;
          /** A parent picked whole: "Electronics (all)". */
          isWholeBranch: boolean;
      }
    | { kind: 'unknown'; id: string };

/** The picked values, "Uncategorized" first, each branch named once. */
export const describeCategorySelection = (
    value: CategoryFilterValue,
    tree: CategoryTree,
): CategorySelectionItem[] => [
    ...(value.noCategory ? [{ kind: 'uncategorized' } as const] : []),
    ...normalizeCategoryIds(value.categoryIds, tree).map(
        (id): CategorySelectionItem => {
            const node = tree.byId.get(id);
            return node
                ? {
                      kind: 'category',
                      id,
                      name: node.name,
                      isWholeBranch: node.childIds.length > 0,
                  }
                : { kind: 'unknown', id };
        },
    ),
];

/**
 * How a picked value reads, the same in the picker's tags and in the tags row:
 * a name as it is, or a copy key for the View to translate — "Uncategorized",
 * a parent picked whole as "Electronics (all)", an unknown id as "Unknown".
 */
export const categorySelectionLabel = (
    item: CategorySelectionItem,
): FilterWords => {
    switch (item.kind) {
        case 'uncategorized':
            return { key: `${FILTERS_COPY}.uncategorized` };
        case 'category':
            return item.isWholeBranch
                ? {
                      key: `${FILTERS_COPY}.categoryWholeBranch`,
                      values: { name: item.name },
                  }
                : { text: item.name };
        case 'unknown':
            return { key: `${FILTERS_COPY}.unknownValue` };
    }
};

/** The picker key a selection item's tag carries. */
export const categorySelectionKey = (item: CategorySelectionItem): string =>
    item.kind === 'uncategorized' ? NO_CATEGORY_KEY : item.id;

/** Removes the values whose tags were removed, by their picker keys. */
export const removeCategorySelection = (
    value: CategoryFilterValue,
    keys: ReadonlySet<unknown>,
    tree: CategoryTree,
): CategoryFilterValue => ({
    categoryIds: normalizeCategoryIds(value.categoryIds, tree).filter(
        (id) => !keys.has(id),
    ),
    noCategory: value.noCategory && !keys.has(NO_CATEGORY_KEY),
});

/** Compares letters alone, as the brand picker's search does: no case, no accents. */
const SEARCH_COLLATOR = new Intl.Collator(undefined, {
    usage: 'search',
    sensitivity: 'base',
});

/**
 * Where `search` sits in `text`, ignoring case and accents; `null` for a
 * blank search or a miss. Slides a window of the search's length along the
 * name, so the span it returns is the name's own text to set in bold.
 */
export const findCategoryMatch = (
    text: string,
    search: string,
): { start: number; end: number } | null => {
    const needle = search.trim();
    if (!needle) {
        return null;
    }
    for (let start = 0; start + needle.length <= text.length; start += 1) {
        const end = start + needle.length;
        if (SEARCH_COLLATOR.compare(text.slice(start, end), needle) === 0) {
            return { start, end };
        }
    }
    return null;
};

export type CategorySearchMatch = {
    node: CategoryTreeNode;
    match: { start: number; end: number };
};

/** Matches that share a parent, headed by the names above them. */
export type CategorySearchGroup = {
    /** The shared parent's id; `''` for top-level matches. */
    key: string;
    /** "Grocery › Snacks" as its parts; empty for top-level matches. */
    path: string[];
    matches: CategorySearchMatch[];
};

/**
 * The categories whose names contain `search`, grouped under the path above
 * them so same-named categories can be told apart ("Grocery › Snacks"). Groups
 * come in the order their first match does in the tree.
 */
export const searchCategoryTree = (
    tree: CategoryTree,
    search: string,
): { groups: CategorySearchGroup[]; count: number } => {
    const groups = new Map<string, CategorySearchGroup>();
    let count = 0;
    for (const node of tree.nodes) {
        const match = findCategoryMatch(node.name, search);
        if (!match) {
            continue;
        }
        const key = node.parentId ?? '';
        const group = groups.get(key) ?? {
            key,
            path: node.ancestorNames,
            matches: [],
        };
        group.matches.push({ node, match });
        groups.set(key, group);
        count += 1;
    }
    return { groups: [...groups.values()], count };
};
